<template>
	<header-block title="Payslips"></header-block>
	<div class="chart-container" v-if="chartData">
		<Bar :data="chartData" :options="chartOptions"></Bar>
	</div>
	<div v-else>
		<v-skeleton-loader type="card" :loading="true"></v-skeleton-loader>
	</div>

	<div v-if="selectedPayslip" class="breakdown-section">
		<div class="breakdown-header">
			<h2>Breakdown for {{ selectedMonthName }}</h2>
		</div>
		<div class="breakdown-grid">
			<div class="breakdown-item gross-pay">
				<span class="label">Gross Pay</span>
				<span class="value">{{ formatter.format(selectedPayslip.grossPay) }}</span>
			</div>
			<div class="breakdown-item salary-sacrifice">
				<span class="label">Salary Sacrifice</span>
				<span class="value">{{ formatter.format(selectedPayslip.salarySacrifice) }}</span>
			</div>
			<div class="breakdown-item national-insurance">
				<span class="label">National Insurance</span>
				<span class="value">{{ formatter.format(selectedPayslip.ni) }}</span>
			</div>
			<div class="breakdown-item tax">
				<span class="label">Tax</span>
				<span class="value">{{ formatter.format(selectedPayslip.tax) }}</span>
			</div>
			<div class="breakdown-item net-pay">
				<span class="label">Net Pay</span>
				<span class="value">{{ formatter.format(selectedPayslip.netPay) }}</span>
			</div>
		</div>
		<MGButton
			text="View Payslip"
			small
			arrow
			@click="viewPayslip"
		/>
	</div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';
import Services from '../mixins/Services';
import HeaderBlock from './elements/HeaderBlock.vue';
import MGButton from './elements/MGButton.vue';

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

export default {
	name: 'PayslipsPage',
	components: { Bar, HeaderBlock, MGButton },
	mixins: [Services],
	data() {
		return {
			payslips: [],
			chartData: null,
			selectedPayslip: null,
			chartOptions: {
				responsive: true,
				maintainAspectRatio: false,
				animation: {
					duration: 750,
					easing: 'easeInOutQuart',
				},
				interaction: {
					intersect: false,
					mode: 'index',
				},
				onClick: (event, elements, chart) => {
					if (elements && elements.length > 0) {
						this.handleChartClick(event, elements);
						return;
					}
					
					const canvasPosition = chart.canvas.getBoundingClientRect();
					const mouseX = event.x - canvasPosition.left;
					
					const xScale = chart.scales.x;
					const labelIndex = xScale.getValueForPixel(mouseX);
					
					if (labelIndex !== undefined && labelIndex >= 0 && labelIndex < chart.data.labels.length) {
						this.handleChartClick(event, [{ index: labelIndex }]);
					}
				},
				scales: {
					x: {
						stacked: true,
						grid: {
							display: false,
						},
						ticks: {
							font: {
								size: 13,
								weight: '500',
							},
							color: '#64748b',
						},
					},
					y: {
						stacked: true,
						beginAtZero: true,
						border: {
							display: false,
						},
						grid: {
							color: '#f1f5f9',
							lineWidth: 1,
						},
						ticks: {
							display: true,
							maxTicksLimit: this.small ? 3 : 5,
							callback: (value) => {
								if (value >= 1000) {
									return this.formatter.format(Math.round(value / 1000), true, false) + 'k';
								}
								return this.formatter.format(value, true, false);
							}
						}
					}
				},
				plugins: {
					legend: {
						display: false
					},
					tooltip: {
						enabled: false
					}
				}
			},
		};
	},
	computed: {
		selectedMonthName() {
			if (!this.selectedPayslip || !this.selectedPayslip.original.meta) return '';
			
			try {
				const metaData = JSON.parse(this.selectedPayslip.original.meta);
				
				const dateStr = metaData['Period ending:'];
				if (!dateStr) return '';
				
				const [day, month, year] = dateStr.split('/');
				const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
				return date.toLocaleString('default', { month: 'long', year: 'numeric' });
			} catch (e) {
				console.error('Error parsing month name:', e);
				return '';
			}
		}
	},
	mounted() {
		this.loadPayslips();
	},
	methods: {
		viewPayslip() {
			if (this.selectedPayslip && this.selectedPayslip.original && this.selectedPayslip.original.id) {
				this.$router.push(`/documents/${this.selectedPayslip.original.id}`);
			}
		},
		loadPayslips() {
			this._getDocuments(() => {
				this.payslips = this.documents.filter(doc => doc.documentType && doc.documentType.toLowerCase() === 'payslip');
				this.prepareChartData();
			});
		},
		prepareChartData() {
			const labels = [];
			const netData = [];
			const taxData = [];
			const salarySacrificeData = [];
			const nationalInsuranceData = [];
			
			const now = new Date();
			const currentYear = now.getFullYear();
			const currentMonth = now.getMonth(); // 0-indexed

			let latestPayslipData = null;

			// Generate last 6 months
			for (let i = 5; i >= 0; i--) {
				const targetDate = new Date(currentYear, currentMonth - i, 1);
				const monthName = targetDate.toLocaleString('default', { month: 'short' });
				const targetYear = targetDate.getFullYear();
				const targetMonth = targetDate.getMonth() + 1; // Convert to 1-indexed
				
				labels.push(monthName);

				let foundPayslip = null;
				for (const payslip of this.payslips) {
					if (!payslip.meta) continue;
					
					try {
						const meta = JSON.parse(payslip.meta);
						const periodEndingStr = meta['Period ending:'];
						if (!periodEndingStr) continue;
						
						const parts = periodEndingStr.split('/');
						const payslipMonth = parseInt(parts[1], 10);
						const payslipYear = parseInt(parts[2], 10);
						
						if (payslipMonth === targetMonth && payslipYear === targetYear) {
							foundPayslip = payslip;
							break;
						}
					} catch (e) {
						console.error('Error parsing payslip meta:', e);
					}
				}

				if (foundPayslip) {
					try {
						const meta = JSON.parse(foundPayslip.meta);
						
						const grossStr = meta['Gross earnings:'] || '£0';
						const grossAmount = parseFloat(grossStr.replace(/[£,]/g, '')) || 0;
						
						const taxableStr = meta['Taxable Earnings:'] || '£0';
						const taxableAmount = parseFloat(taxableStr.replace(/[£,]/g, '')) || 0;
						
						const netStr = meta['Net Payment:'] || '£0';
						const netAmount = parseFloat(netStr.replace(/[£,]/g, '')) || 0;
						
						const deductionsStr = meta['Deductions'] || '';
						const niMatch = deductionsStr.match(/National Insurance £([\d,]+\.\d+)/);
						const niAmount = niMatch ? parseFloat(niMatch[1].replace(/,/g, '')) : 0;
						
						const salarySacrificeAmount = grossAmount - taxableAmount;
						const totalDeductions = taxableAmount - netAmount;
						const taxAmount = totalDeductions - niAmount;

						netData.push(netAmount);
						taxData.push(taxAmount);
						salarySacrificeData.push(salarySacrificeAmount);
						nationalInsuranceData.push(niAmount);

						// Always update to keep the most recent payslip
						latestPayslipData = {
							grossPay: grossAmount,
							salarySacrifice: salarySacrificeAmount,
							ni: niAmount,
							tax: taxAmount,
							netPay: netAmount,
							original: foundPayslip
						};
					} catch (e) {
						console.error('Error processing payslip data:', e);
						netData.push(0);
						taxData.push(0);
						salarySacrificeData.push(0);
						nationalInsuranceData.push(0);
					}
				} else {
					netData.push(0);
					taxData.push(0);
					salarySacrificeData.push(0);
					nationalInsuranceData.push(0);
				}
			}

			// Set the most recent payslip as selected if one was found
			if (latestPayslipData) {
				this.selectedPayslip = latestPayslipData;
			}

			this.chartData = {
				labels,
				datasets: [
					{
						label: 'Net Pay',
						data: netData,
						backgroundColor: '#6ee7b7',
						hoverBackgroundColor: '#34d399',
					},
					{
						label: 'Tax',
						data: taxData,
						backgroundColor: '#fca5a5',
						hoverBackgroundColor: '#f87171',
					},
					{
						label: 'Salary Sacrifice',
						data: salarySacrificeData,
						backgroundColor: '#93c5fd',
						hoverBackgroundColor: '#60a5fa',
					},
					{
						label: 'National Insurance',
						data: nationalInsuranceData,
						backgroundColor: '#fcd34d',
						hoverBackgroundColor: '#fbbf24',
					},
				],
			};
		},
		handleChartClick(event, elements) {
			if (!elements || elements.length === 0) return;
			
			const chartElement = elements[0];
			const index = chartElement.index;
			
			const now = new Date();
			const currentYear = now.getFullYear();
			const currentMonth = now.getMonth();
			
			// Calculate the target month based on the clicked bar index
			const targetDate = new Date(currentYear, currentMonth - (5 - index), 1);
			const targetYear = targetDate.getFullYear();
			const targetMonth = targetDate.getMonth() + 1; // Convert to 1-indexed
			
			// Find payslip for this month
			let foundPayslip = null;
			for (const payslip of this.payslips) {
				if (!payslip.meta) continue;
				
				try {
					const meta = JSON.parse(payslip.meta);
					const periodEndingStr = meta['Period ending:'];
					if (!periodEndingStr) continue;
					
					const parts = periodEndingStr.split('/');
					const payslipMonth = parseInt(parts[1], 10);
					const payslipYear = parseInt(parts[2], 10);
					
					if (payslipMonth === targetMonth && payslipYear === targetYear) {
						foundPayslip = payslip;
						break;
					}
				} catch (e) {
					console.error('Error parsing payslip meta:', e);
				}
			}
			
			if (foundPayslip) {
				try {
					const meta = JSON.parse(foundPayslip.meta);
					
					const grossStr = meta['Gross earnings:'] || '£0';
					const grossAmount = parseFloat(grossStr.replace(/[£,]/g, '')) || 0;
					
					const taxableStr = meta['Taxable Earnings:'] || '£0';
					const taxableAmount = parseFloat(taxableStr.replace(/[£,]/g, '')) || 0;
					
					const netStr = meta['Net Payment:'] || '£0';
					const netAmount = parseFloat(netStr.replace(/[£,]/g, '')) || 0;
					
					const deductionsStr = meta['Deductions'] || '';
					const niMatch = deductionsStr.match(/National Insurance £([\d,]+\.\d+)/);
					const niAmount = niMatch ? parseFloat(niMatch[1].replace(/,/g, '')) : 0;
					
					const salarySacrificeAmount = grossAmount - taxableAmount;
					const totalDeductions = taxableAmount - netAmount;
					const taxAmount = totalDeductions - niAmount;

					this.selectedPayslip = {
						grossPay: grossAmount,
						salarySacrifice: salarySacrificeAmount,
						ni: niAmount,
						tax: taxAmount,
						netPay: netAmount,
						original: foundPayslip
					};
				} catch (e) {
					console.error('Error processing clicked payslip:', e);
				}
			}
		}
	}
};
</script>

<style scoped>
.chart-container {
	position: relative;
	height: 30vh;
	padding: 24px;
	background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
	border-radius: 16px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
	margin: 16px;
	cursor: pointer;
	transition: all 0.3s ease;
}

.chart-container:hover {
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
	transform: translateY(-2px);
}

.breakdown-section {
	padding: 16px;
	margin: 16px;
	background: white;
	border-radius: 16px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.breakdown-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
}

.breakdown-section h2 {
	margin-bottom: 0;
	font-size: 1.1em;
	font-weight: 600;
	color: #0f172a;
}

.view-document-btn {
	text-transform: none;
	font-weight: 500;
}

.breakdown-grid {
	display: grid;
	gap: 8px;
}

.breakdown-item {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 16px;
	border-radius: 10px;
	transition: all 0.2s ease;
	border-left: 3px solid transparent;
}

.breakdown-item:hover {
	transform: translateX(4px);
}

.breakdown-item.gross-pay {
	background: linear-gradient(135deg, rgba(100, 116, 139, 0.08) 0%, rgba(100, 116, 139, 0.04) 100%);
	border-left-color: #64748b;
}

.breakdown-item.salary-sacrifice {
	background: linear-gradient(135deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.04) 100%);
	border-left-color: #3b82f6;
}

.breakdown-item.national-insurance {
	background: linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(245, 158, 11, 0.04) 100%);
	border-left-color: #f59e0b;
}

.breakdown-item.tax {
	background: linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(239, 68, 68, 0.04) 100%);
	border-left-color: #ef4444;
}

.breakdown-item.net-pay {
	background: linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(16, 185, 129, 0.06) 100%);
	border-left-color: #10b981;
	font-size: 1.05em;
	box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
}

.breakdown-item .label {
	font-weight: 600;
	color: #334155;
	font-size: 0.9em;
}

.breakdown-item .value {
	font-weight: 700;
	color: #0f172a;
	font-size: 1em;
	font-variant-numeric: tabular-nums;
}

.breakdown-item.net-pay .label,
.breakdown-item.net-pay .value {
	font-weight: 700;
}

.breakdown-item.net-pay .value {
	color: #059669;
	font-size: 1.1em;
}
</style>
